
import React, { useState, useEffect } from "react";
import * as openpgp from "openpgp";
import { motion } from "framer-motion";
import "./PGPDecryptor.css";
import { fixArmoredMessage, fixArmoredPrivateKey } from "../utils/pgpHelpers";


const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

const PGPDecryptor = ({ prefilledEncrypted = "", setPrefilledEncrypted }) => {
  const [privateKeyArmored, setPrivateKeyArmored] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [encryptedMessage, setEncryptedMessage] = useState(prefilledEncrypted);
  const [decryptedMessage, setDecryptedMessage] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (prefilledEncrypted) {
      setEncryptedMessage(prefilledEncrypted);
      if (setPrefilledEncrypted) {
        setPrefilledEncrypted("");
      }
    }
  }, [prefilledEncrypted, setPrefilledEncrypted]);

  const handleDownload = () => {
    const blob = new Blob([decryptedMessage], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "decrypted_message.txt";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDecrypt = async () => {
    try {
      // Normalize and parse the armored private key.
      const sanitizedKey = fixArmoredPrivateKey(privateKeyArmored);
      const privateKey = await openpgp.readPrivateKey({
        armoredKey: sanitizedKey,
      });

      // Decrypt the private key using the provided passphrase.
      const decryptedPrivateKey = await openpgp.decryptKey({
        privateKey,
        passphrase,
      });

      // Fix the encrypted message formatting.
      const fixedEncryptedMessage = fixArmoredMessage(encryptedMessage);

      // Parse the fixed encrypted message.
      const message = await openpgp.readMessage({
        armoredMessage: fixedEncryptedMessage,
      });

      // Decrypt the message using the decrypted private key.
      const { data: decrypted } = await openpgp.decrypt({
        message,
        decryptionKeys: decryptedPrivateKey,
      });

      setDecryptedMessage(decrypted);
      setError("");

      setCopied(false);
    } catch (err) {
      setError("Decryption failed: " + err.message);
      setDecryptedMessage("");
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(decryptedMessage).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <motion.div
      className="pgp-container card"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <h1>PGP Decryption</h1>
      <div className="form-group">
        <label htmlFor="privateKey">Private Key</label>
        <textarea
          id="privateKey"
          value={privateKeyArmored}
          onChange={(e) => setPrivateKeyArmored(e.target.value)}
          rows="10"
          placeholder="Paste your armored private key here..."
        />
      </div>
      <div className="form-group">
        <label htmlFor="passphrase">Passphrase</label>
        <input
          id="passphrase"
          type="password"
          value={passphrase}
          onChange={(e) => setPassphrase(e.target.value)}
          placeholder="Enter your passphrase..."
        />
      </div>
      <div className="form-group">
        <label htmlFor="encryptedMessage">Encrypted Message</label>
        <textarea
          id="encryptedMessage"
          value={encryptedMessage}
          onChange={(e) => setEncryptedMessage(e.target.value)}
          rows="10"
          placeholder="Paste the PGP encrypted message here..."
        />
      </div>
      <button className="btn" onClick={handleDecrypt}>
        Decrypt
      </button>
      {error && <p className="feedback error">{error}</p>}
      {decryptedMessage && (
        <div className="result">
          <h2>Decrypted Message</h2>
          <pre className="message-box">{decryptedMessage}</pre>
          <button className="btn" onClick={handleCopy}>
            Copy to Clipboard
          </button>
          <button className="btn" onClick={handleDownload}>
            Download
          </button>

          {copied && <p className="feedback">Copied!</p>}
        </div>
      )}
    </motion.div>
  );
};

export default PGPDecryptor;
